import { zodResolver } from "@hookform/resolvers/zod";
import { Check, X } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
	type ProductionWhitelistFormData,
	productionWhitelistSchema,
} from "../schemas/productionWhitelist";
import { COUNTRIES, type Wallet } from "../types";
import FieldError from "./FieldError";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import ValidationBanner from "./ValidationBanner";

interface ProductionWhitelistFormProps {
	wallet: Wallet;
	isOpen: boolean;
	onClose: () => void;
	onSave: (data: Partial<Wallet>) => void;
}

const ProductionWhitelistForm: React.FC<ProductionWhitelistFormProps> = ({
	wallet,
	isOpen,
	onClose,
	onSave,
}) => {
	const [countrySearch, setCountrySearch] = useState("");
	const [showCountryDropdown, setShowCountryDropdown] = useState(false);
	const bannerRef = useRef<HTMLDivElement>(null);

	const form = useForm<ProductionWhitelistFormData>({
		resolver: zodResolver(productionWhitelistSchema),
		defaultValues: {
			custodialType: (wallet.production_custodial_type ||
				wallet.custodial_type) as "Custodial" | "Non-Custodial" | "Both",
			tomlFileDomain: wallet.production_toml_file_domain || "",
			homeDomain: wallet.production_home_domain || wallet.home_domain || "",
			fundingAddress:
				wallet.production_funding_address || wallet.funding_address || "",
			depositAddress: wallet.production_deposit_address || "",
			authAddress: wallet.production_auth_address || "",
			excludedCountries: wallet.excluded_countries || [],
		},
		mode: "onSubmit",
	});

	const custodialType = form.watch("custodialType");
	const excludedCountries = form.watch("excludedCountries");
	const isCustodial = custodialType === "Custodial" || custodialType === "Both";
	const isNonCustodial =
		custodialType === "Non-Custodial" || custodialType === "Both";

	useEffect(() => {
		if (isOpen) {
			form.reset({
				custodialType: (wallet.production_custodial_type ||
					wallet.custodial_type) as "Custodial" | "Non-Custodial" | "Both",
				tomlFileDomain: wallet.production_toml_file_domain || "",
				homeDomain: wallet.production_home_domain || wallet.home_domain || "",
				fundingAddress:
					wallet.production_funding_address || wallet.funding_address || "",
				depositAddress: wallet.production_deposit_address || "",
				authAddress: wallet.production_auth_address || "",
				excludedCountries: wallet.excluded_countries || [],
			});
		}
	}, [isOpen, wallet, form]);

	const filteredCountries = COUNTRIES.filter(
		(country) =>
			country.toLowerCase().includes(countrySearch.toLowerCase()) &&
			!excludedCountries.includes(country),
	);

	const handleAddCountry = (country: string) => {
		if (!excludedCountries.includes(country)) {
			form.setValue("excludedCountries", [...excludedCountries, country]);
		}
		setCountrySearch("");
		setShowCountryDropdown(false);
	};

	const handleRemoveCountry = (country: string) => {
		form.setValue(
			"excludedCountries",
			excludedCountries.filter((c) => c !== country),
		);
	};

	const onSubmit = (data: ProductionWhitelistFormData) => {
		onSave({
			production_home_domain: data.homeDomain,
			production_funding_address: data.fundingAddress,
			production_deposit_address: data.depositAddress,
			production_auth_address: data.authAddress,
			production_toml_file_domain: data.tomlFileDomain,
			production_custodial_type: data.custodialType,
			excluded_countries: data.excludedCountries,
		});
		onClose();
	};

	const onInvalid = () => {
		bannerRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const errors = form.formState.errors;
	const hasErrors = Object.keys(errors).length > 0;
	const errorMessages = hasErrors
		? Object.values(errors)
				.filter((e): e is { message?: string } => !!e && typeof e === "object")
				.map((e) => e.message)
				.filter((m): m is string => !!m)
		: [];

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-[700px]">
				<DialogHeader>
					<DialogTitle>Production Whitelisting Information</DialogTitle>
					<DialogDescription>
						Manage information needed for production whitelisting. You can save
						partial information and complete it later.
					</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={form.handleSubmit(onSubmit, onInvalid)}
					className="flex flex-col"
				>
					<div
						ref={bannerRef}
						className="max-h-[70vh] space-y-6 overflow-y-auto pr-1"
					>
						{hasErrors && (
							<ValidationBanner
								message="Please fix the errors below before saving."
								fields={errorMessages}
								aria-live="polite"
							/>
						)}

						{/* Custodial Type - Show First */}
						<div className="space-y-1.5">
							<Label htmlFor="custodialType">
								Custodial Type{" "}
								<span className="text-(--color-error-500)">*</span>
							</Label>
							<Controller
								control={form.control}
								name="custodialType"
								render={({ field }) => (
									<Select value={field.value} onValueChange={field.onChange}>
										<SelectTrigger
											id="custodialType"
											aria-invalid={!!errors.custodialType}
											aria-describedby={
												errors.custodialType ? "custodialType-error" : undefined
											}
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="Custodial">Custodial</SelectItem>
											<SelectItem value="Non-Custodial">
												Non-Custodial
											</SelectItem>
											<SelectItem value="Both">Both</SelectItem>
										</SelectContent>
									</Select>
								)}
							/>
							{errors.custodialType?.message && (
								<FieldError
									id="custodialType-error"
									message={errors.custodialType.message}
								/>
							)}
							<p className="mt-1.5 text-(--color-muted-foreground) text-sm">
								Default from wallet profile: {wallet.custodial_type}
							</p>
						</div>

						{/* TOML File Domain - Only for Non-Custodial */}
						{isNonCustodial && (
							<div className="space-y-1.5">
								<Label htmlFor="tomlFileDomain">
									TOML File Domain{" "}
									<span className="text-(--color-error-500)">*</span>
								</Label>
								<Input
									id="tomlFileDomain"
									type="text"
									{...form.register("tomlFileDomain")}
									value={form.watch("tomlFileDomain") ?? ""}
									onChange={(e) =>
										form.setValue("tomlFileDomain", e.target.value)
									}
									placeholder="e.g., example.com/.well-known/stellar.toml"
									className={
										errors.tomlFileDomain
											? "border-(--color-error-500)"
											: undefined
									}
									aria-invalid={!!errors.tomlFileDomain}
									aria-describedby={
										errors.tomlFileDomain ? "tomlFileDomain-error" : undefined
									}
								/>
								{errors.tomlFileDomain?.message && (
									<FieldError
										id="tomlFileDomain-error"
										message={errors.tomlFileDomain.message}
									/>
								)}
								<p className="text-(--color-muted-foreground) text-sm">
									Required for Non-Custodial wallets
								</p>
							</div>
						)}

						{/* Home Domain - Required for Custodial */}
						{isCustodial && (
							<div className="space-y-1.5">
								<Label htmlFor="homeDomain">
									Home Domain{" "}
									<span className="text-(--color-error-500)">*</span>
								</Label>
								<Input
									id="homeDomain"
									type="text"
									{...form.register("homeDomain")}
									value={form.watch("homeDomain") ?? ""}
									onChange={(e) => form.setValue("homeDomain", e.target.value)}
									placeholder="e.g., wallet.example.com"
									className={
										errors.homeDomain ? "border-(--color-error-500)" : undefined
									}
									aria-invalid={!!errors.homeDomain}
									aria-describedby={
										errors.homeDomain ? "homeDomain-error" : undefined
									}
								/>
								{errors.homeDomain?.message && (
									<FieldError
										id="homeDomain-error"
										message={errors.homeDomain.message}
									/>
								)}
								<p className="text-(--color-muted-foreground) text-sm">
									Required for Custodial wallets
								</p>
							</div>
						)}

						{/* Funding Address - Required for Custodial */}
						{isCustodial && (
							<div className="space-y-1.5">
								<Label htmlFor="fundingAddress">
									Funding Address{" "}
									<span className="text-(--color-error-500)">*</span>
								</Label>
								<Input
									id="fundingAddress"
									type="text"
									{...form.register("fundingAddress")}
									value={form.watch("fundingAddress") ?? ""}
									onChange={(e) =>
										form.setValue("fundingAddress", e.target.value)
									}
									placeholder="Enter Stellar funding address (G...)"
									className={
										errors.fundingAddress
											? "border-(--color-error-500)"
											: undefined
									}
									aria-invalid={!!errors.fundingAddress}
									aria-describedby={
										errors.fundingAddress ? "fundingAddress-error" : undefined
									}
								/>
								{errors.fundingAddress?.message && (
									<FieldError
										id="fundingAddress-error"
										message={errors.fundingAddress.message}
									/>
								)}
								<p className="text-(--color-muted-foreground) text-sm">
									Required for Custodial wallets
								</p>
							</div>
						)}

						{/* Deposit Address - Required for Custodial */}
						{isCustodial && (
							<div className="space-y-1.5">
								<Label htmlFor="depositAddress">
									Deposit Address{" "}
									<span className="text-(--color-error-500)">*</span>
								</Label>
								<Input
									id="depositAddress"
									type="text"
									{...form.register("depositAddress")}
									value={form.watch("depositAddress") ?? ""}
									onChange={(e) =>
										form.setValue("depositAddress", e.target.value)
									}
									placeholder="Enter deposit address"
									className={
										errors.depositAddress
											? "border-(--color-error-500)"
											: undefined
									}
									aria-invalid={!!errors.depositAddress}
									aria-describedby={
										errors.depositAddress ? "depositAddress-error" : undefined
									}
								/>
								{errors.depositAddress?.message && (
									<FieldError
										id="depositAddress-error"
										message={errors.depositAddress.message}
									/>
								)}
								<p className="text-(--color-muted-foreground) text-sm">
									Required for Custodial wallets
								</p>
							</div>
						)}

						{/* Authentication Address - Required for Custodial */}
						{isCustodial && (
							<div className="space-y-1.5">
								<Label htmlFor="authAddress">
									Authentication Address{" "}
									<span className="text-(--color-error-500)">*</span>
								</Label>
								<Input
									id="authAddress"
									type="text"
									{...form.register("authAddress")}
									value={form.watch("authAddress") ?? ""}
									onChange={(e) => form.setValue("authAddress", e.target.value)}
									placeholder="Enter authentication address"
									className={
										errors.authAddress
											? "border-(--color-error-500)"
											: undefined
									}
									aria-invalid={!!errors.authAddress}
									aria-describedby={
										errors.authAddress ? "authAddress-error" : undefined
									}
								/>
								{errors.authAddress?.message && (
									<FieldError
										id="authAddress-error"
										message={errors.authAddress.message}
									/>
								)}
								<p className="text-(--color-muted-foreground) text-sm">
									Required for Custodial wallets
								</p>
							</div>
						)}

						{/* Excluded Countries */}
						<div className="space-y-1.5">
							<Label>Excluded Countries (Optional)</Label>
							<p className="mb-2.5 text-(--color-muted-foreground) text-sm">
								Select countries where the service should NOT be available
							</p>

							{/* Selected Countries */}
							{excludedCountries.length > 0 && (
								<div className="mb-3 flex flex-wrap gap-2 rounded-(--radius) border border-(--color-border) bg-(--color-muted) p-3">
									{excludedCountries.map((country) => (
										<span
											key={country}
											className="inline-flex items-center gap-1.5 rounded-2xl bg-(--color-error-500) px-2.5 py-1 font-medium text-sm text-white"
										>
											{country}
											<button
												type="button"
												onClick={() => handleRemoveCountry(country)}
												className="flex cursor-pointer items-center border-none bg-transparent p-0 text-white"
												aria-label={`Remove ${country}`}
											>
												<X size={14} />
											</button>
										</span>
									))}
								</div>
							)}

							{/* Country Search */}
							<div className="relative">
								<Input
									type="text"
									value={countrySearch}
									onChange={(e) => {
										setCountrySearch(e.target.value);
										setShowCountryDropdown(true);
									}}
									onFocus={() => setShowCountryDropdown(true)}
									placeholder="Search and select countries to exclude..."
								/>

								{showCountryDropdown &&
									countrySearch &&
									filteredCountries.length > 0 && (
										<div className="absolute top-full right-0 left-0 z-10 max-h-[200px] overflow-y-auto rounded-b-(--radius) border border-(--color-border) border-t-0 bg-(--color-card) shadow-md">
											{filteredCountries.map((country) => (
												// biome-ignore lint/a11y/noStaticElementInteractions: Country selection needs click handler
												// biome-ignore lint/a11y/useKeyWithClickEvents: Keyboard navigation not needed for country list
												<div
													key={country}
													onClick={() => handleAddCountry(country)}
													className="cursor-pointer border-(--color-border) border-b px-3.5 py-2.5 transition-colors last:border-b-0 hover:bg-(--color-muted)"
												>
													{country}
												</div>
											))}
										</div>
									)}
							</div>
						</div>
					</div>

					<DialogFooter className="mt-6 border-(--color-border) border-t pt-4">
						<Button type="button" variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit">
							<Check size={16} />
							Save Information
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default ProductionWhitelistForm;
